import {Optional} from '@othree.io/optional'
import {Stack} from 'aws-cdk-lib'
import {Topic, TopicProps, SubscriptionFilter, ITopic} from 'aws-cdk-lib/aws-sns'
import {IQueue} from 'aws-cdk-lib/aws-sqs'
import {SqsSubscription, SqsSubscriptionProps} from 'aws-cdk-lib/aws-sns-subscriptions'
import {StackAccount} from './core'

/** Sentinel event type automatically included in Chisel filter policies. */
export const HealthCheckEventType = '$$HealthCheckRequested'

/** Sentinel bounded context automatically included in Chisel filter policies. */
export const HealthCheckBC = '$$HealthCheckBC'

export type SubscriptionFilterPolicy = { [key: string]: SubscriptionFilter }

export type TopicInput = Readonly<{
    id: string
    displayName?: string
}>

/**
 * Bounded contexts and event types to include in a Chisel SNS filter policy.
 * Health-check sentinels are appended automatically.
 */
export type ChiselFilterPolicyInput = Readonly<{
    bcs: Array<string>
    eventTypes?: Array<string>
}>

export type SqsSubscriptionInput = Readonly<{
    topic: ITopic
    queue: IQueue
    dlq?: IQueue
}>

/** Creates an SNS topic with optional default and per-call configuration overrides. */
export const withTopic = (deps: {
    defaultConfiguration?: Partial<TopicProps>
    stack: Stack
}) =>
    (input: TopicInput, configuration?: Partial<TopicProps>): ITopic => {
        const topic = new Topic(deps.stack, input.id, {
            ...Optional(deps.defaultConfiguration).orElse({}),
            topicName: input.id,
            displayName: input.displayName,
            ...Optional(configuration).orElse({})
        })

        return topic
    }

/**
 * Builds a subscription filter policy for Chisel event-sourced topics.
 * Automatically appends health-check sentinels to both bounded context and event type allowlists.
 */
export const withChiselFilterPolicy = (input: ChiselFilterPolicyInput): SubscriptionFilterPolicy => {
    const filterPolicy: SubscriptionFilterPolicy = {
        bc: SubscriptionFilter.stringFilter({
            allowlist: [...input.bcs, HealthCheckBC]
        }),
    }

    return Optional(input.eventTypes)
        .map(eventTypes => ({
            ...filterPolicy,
            eventType: SubscriptionFilter.stringFilter({
                allowlist: [...eventTypes, HealthCheckEventType]
            })
        } as SubscriptionFilterPolicy))
        .orElse(filterPolicy)
}

/** Subscribes an SQS queue to an SNS topic with an optional filter policy and dead-letter queue. */
export const withSqsSubscription = (deps: {
    configuration?: SqsSubscriptionProps
    filterPolicy?: SubscriptionFilterPolicy
}) =>
    (input: SqsSubscriptionInput): boolean => {
        input.topic.addSubscription(new SqsSubscription(
            input.queue,
            {
                deadLetterQueue: input.dlq,
                ...Optional(deps.filterPolicy).map(_ => ({filterPolicy: _})).orElse({} as any),
                ...Optional(deps.configuration).orElse({})
            }
        ))

        return true
    }

/** Constructs an SNS topic ARN from account info and topic name. */
export const topicArnFromName = (deps: { account: StackAccount }) =>
    (topicName: string): string => {
        return `arn:aws:sns:${deps.account.region}:${deps.account.accountId}:${topicName}`
    }
